import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Chip from '@material-ui/core/Chip';
import { surgeryService } from '../../../services/surgeryService';
import BillingStatusBadge from '../shared/BillingStatusBadge';
import {
  BILLING_STATUS,
  isBillingCleared,
} from '../shared/constants';

const formatCurrency = (value) =>
  `USD ${Number(value || 0).toLocaleString()}`;

const useStyles = makeStyles(() => ({
  section: {
    marginBottom: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#212121',
  },
  card: {
    border: '1px solid #EEEEEE',
    borderRadius: 8,
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    padding: '14px 16px 0 16px',
  },
  // Table
  tableWrap: {
    overflowX: 'auto',
  },
  tableHead: {
    backgroundColor: '#FAFAFA',
  },
  tableCell: {
    fontSize: '0.82rem',
    padding: '10px 16px',
    color: '#424242',
    borderBottom: '1px solid #F5F5F5',
  },
  tableCellHead: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#9E9E9E',
    padding: '10px 16px',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '1px solid #EEEEEE',
  },
  coveredChip: {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 500,
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
  },
  cashChip: {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 500,
    backgroundColor: '#FFF3E0',
    color: '#E65100',
  },
  // Totals
  totals: {
    padding: '12px 16px 16px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
  },
  totalLabel: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  totalValue: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#212121',
  },
  totalDivider: {
    borderTop: '1px solid #E0E0E0',
    marginTop: 6,
    paddingTop: 8,
  },
  grandTotal: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#212121',
  },
  // Actions
  actionCard: {
    padding: 16,
    borderRadius: 8,
    border: '1px solid #EEEEEE',
    marginBottom: 16,
  },
  actionText: {
    fontSize: '0.82rem',
    color: '#616161',
    marginBottom: 10,
  },
  actionBar: {
    display: 'flex',
    gap: 8,
  },
  // Status notices
  notice: {
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: '0.82rem',
    fontWeight: 500,
  },
  noticeCleared: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    border: '1px solid #C8E6C9',
  },
  noticeCash: {
    backgroundColor: '#FFF8E1',
    color: '#F57F17',
    border: '1px solid #FFE082',
  },
  noticeDeferred: {
    backgroundColor: '#E3F2FD',
    color: '#1565C0',
    border: '1px solid #90CAF9',
  },
  // Activity timeline
  timeline: {
    position: 'relative',
    paddingLeft: 24,
    padding: '8px 16px 16px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 23,
      top: 8,
      bottom: 16,
      width: 2,
      backgroundColor: '#EEEEEE',
    },
  },
  timelineItem: {
    position: 'relative',
    paddingBottom: 14,
    paddingLeft: 16,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  timelineDot: {
    position: 'absolute',
    left: -4,
    top: 4,
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#BDBDBD',
    border: '2px solid #fff',
    boxShadow: '0 0 0 1px #E0E0E0',
  },
  timelineDotFirst: {
    backgroundColor: '#1976D2',
    boxShadow: '0 0 0 1px #BBDEFB',
  },
  timelineText: {
    fontSize: '0.8rem',
    color: '#424242',
    lineHeight: 1.4,
  },
  timelineAmount: {
    fontSize: '0.75rem',
    color: '#757575',
    fontWeight: 500,
  },
  timelineDate: {
    fontSize: '0.7rem',
    color: '#BDBDBD',
    marginTop: 2,
  },
  // Empty / loading
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: '0.82rem',
    color: '#BDBDBD',
    fontStyle: 'italic',
    padding: '20px 16px',
  },
}));

/**
 * BillingSection -- surgery detail billing tab.
 * Shows cost estimate table, action buttons, and activity timeline.
 */
const BillingSection = ({ surgeryId, surgery }) => {
  const classes = useStyles();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!surgeryId) return;
    try {
      const res = await surgeryService.getBillSummary(surgeryId);
      setSummary(res.data || res);
    } catch (err) {
      console.error('Failed to load billing summary', err);
    } finally {
      setLoading(false);
    }
  }, [surgeryId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleCollectPayment = async (newBillingStatus) => {
    setActionLoading(true);
    try {
      await surgeryService.updateBillingStatus({
        surgery_id: surgeryId,
        billing_status: newBillingStatus,
      });
      await fetchSummary();
    } catch (err) {
      console.error('Status update failed', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!summary) {
    return (
      <Paper className={classes.actionCard} elevation={0}>
        <Typography variant="body2" color="textSecondary">
          Unable to load billing information.
        </Typography>
      </Paper>
    );
  }

  const {
    billing_status,
    is_insured,
    items = [],
    total_estimate = 0,
    insurer_pays = 0,
    patient_pays = 0,
    activities = [],
    deferred = false,
    pathway = '',
  } = summary;

  const cleared = isBillingCleared(billing_status);

  return (
    <div className={classes.section}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          Billing Overview
        </Typography>
        <BillingStatusBadge status={billing_status} />
      </div>

      {/* Deferred billing notice for pre-admission inpatient */}
      {deferred && (
        <div className={`${classes.notice} ${classes.noticeDeferred}`}>
          This is an inpatient estimate. Bills will be posted automatically when the patient is admitted.
        </div>
      )}

      {/* Cost Estimate */}
      <Paper className={classes.card} elevation={0}>
        <Typography className={classes.cardTitle}>
          {deferred ? 'Estimate (Pending Admission)' : 'Cost Estimate'}
        </Typography>

        {items.length > 0 ? (
          <>
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableCellHead}>Item</TableCell>
                    <TableCell className={classes.tableCellHead} align="right">Qty</TableCell>
                    <TableCell className={classes.tableCellHead} align="right">Total</TableCell>
                    {is_insured && (
                      <>
                        <TableCell className={classes.tableCellHead} align="right">Insurer</TableCell>
                        <TableCell className={classes.tableCellHead} align="right">Patient</TableCell>
                      </>
                    )}
                    <TableCell className={classes.tableCellHead} align="center">Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={item.bill_id || `est-${idx}`} hover>
                      <TableCell className={classes.tableCell}>{item.description}</TableCell>
                      <TableCell className={classes.tableCell} align="right">{item.quantity}</TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        {formatCurrency(item.total)}
                      </TableCell>
                      {is_insured && (
                        <>
                          <TableCell className={classes.tableCell} align="right">
                            {item.covered ? formatCurrency(item.insurer_amount) : '-'}
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            {formatCurrency(item.patient_amount)}
                          </TableCell>
                        </>
                      )}
                      <TableCell className={classes.tableCell} align="center">
                        {item.covered ? (
                          <Chip label="Covered" size="small" className={classes.coveredChip} />
                        ) : (
                          <Chip label="Cash" size="small" className={classes.cashChip} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className={classes.totals}>
              <div className={classes.totalRow}>
                <span className={classes.totalLabel}>Total Estimate</span>
                <span className={classes.totalValue}>{formatCurrency(total_estimate)}</span>
              </div>
              {is_insured && (
                <>
                  <div className={classes.totalRow}>
                    <span className={classes.totalLabel}>Insurance Covers</span>
                    <span className={classes.totalValue} style={{ color: '#2E7D32' }}>
                      {formatCurrency(insurer_pays)}
                    </span>
                  </div>
                  <div className={`${classes.totalRow} ${classes.totalDivider}`}>
                    <span className={classes.totalLabel} style={{ fontWeight: 600, color: '#424242' }}>
                      Patient Responsibility
                    </span>
                    <span className={classes.grandTotal}>
                      {formatCurrency(patient_pays)}
                    </span>
                  </div>
                </>
              )}
              {!is_insured && (
                <div className={`${classes.totalRow} ${classes.totalDivider}`}>
                  <span className={classes.totalLabel} style={{ fontWeight: 600, color: '#424242' }}>
                    Amount Due
                  </span>
                  <span className={classes.grandTotal}>
                    {formatCurrency(patient_pays)}
                  </span>
                </div>
              )}
            </div>
          </>
        ) : (
          <Typography className={classes.emptyText}>
            No bill items found for this surgery.
          </Typography>
        )}
      </Paper>

      {/* Actions -- insurance co-pay / denied */}
      {!cleared && is_insured && billing_status === BILLING_STATUS.CO_PAY_PENDING && (
        <div className={classes.actionCard}>
          <Typography className={classes.actionText}>
            Co-pay of {formatCurrency(patient_pays)} is due from the patient before surgery can proceed.
          </Typography>
          <div className={classes.actionBar}>
            <button
              className="btn btn-sm sms-btn"
              disabled={actionLoading}
              onClick={() => handleCollectPayment(BILLING_STATUS.CO_PAY_COLLECTED)}
            >
              {actionLoading ? 'Processing...' : 'Record Co-Pay Collection'}
            </button>
          </div>
        </div>
      )}

      {!cleared && is_insured && billing_status === BILLING_STATUS.PRE_AUTH_DENIED && (
        <div className={classes.actionCard}>
          <Typography className={classes.actionText}>
            Pre-authorisation was denied. You can convert this to a cash case.
          </Typography>
          <div className={classes.actionBar}>
            <button
              className="btn btn-sm sms-btn"
              disabled={actionLoading}
              onClick={() => handleCollectPayment(BILLING_STATUS.CASH_UNPAID)}
            >
              Convert to Cash
            </button>
          </div>
        </div>
      )}

      {/* Cash patient notice */}
      {!cleared && !is_insured && (
        <div className={`${classes.notice} ${classes.noticeCash}`}>
          Payment is handled through the {surgery?.pathway === 'inpatient' ? 'inpatient' : 'outpatient'} billing list.
        </div>
      )}

      {/* Cleared notice */}
      {cleared && (
        <div className={`${classes.notice} ${classes.noticeCleared}`}>
          Billing is cleared -- clinical workflow can proceed.
        </div>
      )}

      {/* Activity Timeline */}
      {activities.length > 0 && (
        <Paper className={classes.card} elevation={0}>
          <Typography className={classes.cardTitle}>Activity</Typography>
          <div className={classes.timeline}>
            {activities.map((a, idx) => (
              <div key={a.activity_id} className={classes.timelineItem}>
                <div
                  className={`${classes.timelineDot} ${idx === 0 ? classes.timelineDotFirst : ''}`}
                />
                <Typography className={classes.timelineText}>{a.description}</Typography>
                {a.amount > 0 && (
                  <Typography className={classes.timelineAmount}>
                    {formatCurrency(a.amount)}
                  </Typography>
                )}
                <Typography className={classes.timelineDate}>
                  {a.created_at ? new Date(a.created_at).toLocaleString('en-US') : ''}
                </Typography>
              </div>
            ))}
          </div>
        </Paper>
      )}
    </div>
  );
};

export default BillingSection;
